import Accessibility, {
    AccessibilityEventsKeys,
} from '@src/slide/application/service/accessibility-manager/core/accessibility';
import AccessibilityConstants, { keyboardActions } from '@src/slide/application/service/accessibility-manager/core/accessibility-constants';
import { DIRECTION } from '@src/utils/key-mouse-event/key-map';
import i18n from '@i18next/i18n';
import { IReactionDisposer, reaction } from 'mobx';
import Modal from '@tencent/dui/lib/components/Modal';
import React from 'react';
import { RegionClassName } from '@src/js/constant';
import { convertPxToCm } from '@src/slide/infrastructure/module/independent/unit';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { INoteModel } from '@src/slide/domain/ui-model/element-modeles/Interface/note-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { GroupAccessibilityContent } from './content/model/group';
import { TableAccessibilityContent } from './content/model/table';
import { ElementBaseAccessibilityContent } from './content/model/base';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import {
    IAccessibilityManager, IAccessibilityManagerContentHandler, IAccessibilityManagerServiceDelegates,
} from './accessibility-manager.interface';
import { SelectedModelType } from '@src/slide/application/service/selection/selection-service.interface';

export class AccessibilityManager implements IAccessibilityManager, IAccessibilityManagerContentHandler {
    public isAccessibilityOn = false;
    public readonly Accessibility: typeof Accessibility;
    public services: IAccessibilityManagerServiceDelegates;

    private slideReactionDisposer: IReactionDisposer | null;
    private selectorReactionDisposer: IReactionDisposer | null;
    private hasRegister: boolean;
    private startPlayOff: Function | null;
    private playOff: Function | null;
    private endPlayOff: Function | null;
    private isJustStartPlay: boolean;
    private timer: any;
    private openContent = '';
    private arrowMoveTimer: any;
    private moveSelectedEleWithArrow: any;
    private keyCode: number | null; // 记录当前按键
    private lastText: string; // 记录上一次光标所在行文本

    public constructor() {
        this.Accessibility = Accessibility;
        Accessibility.initWithUid(window.clientVars.tinyid);
        document.getElementsByClassName('manager-wrapper')[0]?.setAttribute('aria-hidden', 'true');
        document.getElementById(RegionClassName.PPT_EDITOR)?.setAttribute('aria-hidden', 'true');
        this.isJustStartPlay = true;

        Accessibility.on(AccessibilityEventsKeys.ACTION_ENABLE, (isEnableState) => {
            this.isAccessibilityOn = isEnableState;
            if (isEnableState) {
                this.registerListener();
            }
        });
    }

    public setServiceDelegates(services: Partial<IAccessibilityManagerServiceDelegates>): void {
        this.services = {
            ...this.services,
            ...services,
        };
        if (Accessibility.isEnable()) {
            this.isAccessibilityOn = true;
            this.registerListener();
        }
    }

    /**
     * 无障碍ui开关
     */
    public showAccessibilityModal(isShortCut?: boolean) {
        const content = this.getModalContent(isShortCut);
        if (Accessibility.isEnable()) {
            Modal.confirm({
                title: i18n.t('无障碍设置'),
                content,
                onOk: () => {
                    this.disable();
                },
                okText: i18n.t('确定'),
                cancelText: i18n.t('取消'),
            });
        } else {
            Modal.confirm({
                title: i18n.t('无障碍设置'),
                content,
                onOk: () => {
                    this.enable();
                },
                okText: i18n.t('确定'),
                cancelText: i18n.t('取消'),
            });
        }
    }

    /**
     * 开启无障碍监听
     */
    public enable() {
        if (!Accessibility.isEnable()) {
            Accessibility.enable();
            this.registerListener();
        }
    }

    /**
     * 关闭无障碍监听
     */
    public disable() {
        if (Accessibility.isEnable()) {
            Accessibility.disable();
            this.removeListener();
        }
    }

    /**
     * 朗读开屏语
     */
    public readOpenContent() {
        this.openContent = i18n.t('欢迎使用腾讯文档。用户可以通过 control 加 反引号 打开或者关闭无障碍。');
        if (Accessibility.isEnable()) {
            this.readSlide(
                this.services.selectionService.currentSlide,
                this.services.selectionService.currentSlideIndex, false,
            );
        } else {
            Accessibility.readStr(this.openContent, true);
        }
        this.openContent = '';
    }

    /**
     * 处理keydown事件
     */
    public handleKeydown(keyCode: number) {
        this.keyCode = keyCode;
    }

    /**
     * 根据上下左右键进行朗读
     */
    public readKeyArrowContent(endOffset: number, text: string) {
        if (!this.keyCode || !text) return;
        const keyCodeContentMap = {
            [DIRECTION.left.keyCode]: this.readLeftKey,
            [DIRECTION.right.keyCode]: this.readRightKey,
            [DIRECTION.up.keyCode]: () => text,
            [DIRECTION.down.keyCode]: () => text,
        };
        if (typeof keyCodeContentMap[this.keyCode] !== 'function') return;
        const content = keyCodeContentMap[this.keyCode](endOffset, text);
        Accessibility.readStr(i18n.t('{0}', { 0: content }));
        this.lastText = text;
    }

    // 注册所有监听器
    private registerListener() {
        if (this.hasRegister || !this.services) return;
        this.hasRegister = true;
        // 监听幻灯片切页
        this.slideReactionDisposer = reaction(
            () => this.services.selectionService.currentSlideIndex,
            (index: number) => {
                this.readSlide(this.services.selectionService.currentSlide, index);
            },
        );
        // 监听编辑
        window.addEventListener('enterEditing', this.handleEnterEditing);
        window.addEventListener('exitEditing', this.handleExitEditing);
        window.addEventListener(keyboardActions.PASTE, this.handlePaste, true);
        // 监听选中事件
        this.selectorReactionDisposer  = reaction(() => this.services
            .selectionService.getSelectedModel(), (multiModel: SelectedModelType) => {
            this.handleSelectModel(multiModel);
        });
        // 监听播放页切换事件
        this.startPlayOff = Accessibility.on(AccessibilityConstants.START_PLAY, () => {
            this.isJustStartPlay = true;
        });
        // 监听开始播放幻灯片事件
        this.playOff = Accessibility.on(AccessibilityConstants.PLAY_INDEX_CHANGE, (index) => {
            this.handlePlayIndexChange(index);
        });
        // 监听结束播放幻灯片事件
        this.endPlayOff = Accessibility.on(AccessibilityConstants.END_PLAY, () => {
            this.readSlide(
                this.services.selectionService.currentSlide,
                this.services.selectionService.currentSlideIndex,
            );
        });
        // 监听上下左右键移动选中元素的事件
        this.moveSelectedEleWithArrow = Accessibility.on(
            AccessibilityConstants.MOVE_SELECTED_ELEMENTS_WITH_ARROW,
            ({ left, top }) => {
                this.handleSelectedEleWithArrow(left, top);
            },
        );
    }

    // 移除所有监听器
    private removeListener() {
        this.hasRegister = false;
        this.slideReactionDisposer?.();
        this.selectorReactionDisposer?.();
        window.removeEventListener('enterEditing', this.handleEnterEditing);
        window.removeEventListener('exitEditing', this.handleExitEditing);
        window.removeEventListener(keyboardActions.PASTE, this.handlePaste);
        this.startPlayOff?.();
        this.playOff?.();
        this.endPlayOff?.();
        this.moveSelectedEleWithArrow?.();
    }

    // 读取幻灯片内容
    private readSlide(curSlide?: ISlideModel, curSlideIndex = 1, isPlayIndexChanged?: boolean) {
        if (curSlide) {
            let content =  '';
            const { slideCount } = this.services.uiDataService.presentation;
            curSlide.children.forEach((element) => {
                content += this.getAccessibilityContent(element);
            });
            const noteContent = this.collectNote(curSlide.noteData);
            let details = `${this.openContent}${
                i18n.t('第{0}张幻灯片，共{1}张。', { 0: curSlideIndex + 1, 1: slideCount })
            }${content}${noteContent}。${
                i18n.t('页面上有{0}个对象，此页已结束。', { 0: curSlide.children.length })}`;
            if (curSlideIndex + 1 === slideCount) {
                details += i18n.t('已到最后一页。');
            }
            if (isPlayIndexChanged) {
                details = i18n.t('页面已更改。{0}', { 0: details });
            }
            Accessibility.readStr(details);
        }
    }

    // 收集注释信息
    private collectNote(note: INoteModel) {
        let tempNote = '';
        if (note.id) {
            tempNote += `${i18n.t('还有演讲者备注：')}${this.getAccessibilityContent(note.noteTextBody, false, '')}`;
        }
        return tempNote;
    }

    // 监听是否进入编辑状态
    private handleEnterEditing = () => {
        const selectedModel = this.services.selectionService.getSelectedModel();
        if (selectedModel?.type === UIModelType.TABLE) {
            const tableModel = (selectedModel as ITableModel);
            const row = tableModel.selectedRange?.rowEnd ?? 0;
            const col = tableModel.selectedRange?.colEnd ?? 0;
            const detail = this.getAccessibilityContent(tableModel.tableItems[row][col], false, '');
            Accessibility.readStr(i18n.t('第{0}行，第{1}列，{2}', { 0: row + 1, 1: col + 1, 2: detail }));
        } else {
            Accessibility.readStr(i18n.t('已进入文本选择'));
        }
    };

    private handleExitEditing = () => {
        this.lastText = '';
        this.keyCode = null;
        Accessibility.readStr(i18n.t('已退出文本选择'));
    };

    // 监听已选中元素
    private handleSelectModel(selectedModel: SelectedModelType) {
        if (!selectedModel || selectedModel?.type === UIModelType.EMPTY) {
            Accessibility.readStr('');
        } else {
            let selectedContent: string;
            if (selectedModel.type === UIModelType.MULTIPLY) {
                const { selectedModels } = selectedModel as IMultiplyModel;
                const { length } = selectedModels;
                selectedContent = i18n.t('共选中{0}个对象。', { 0: length });
                selectedModels.forEach((element) => {
                    selectedContent += `${i18n.t('已选中，')}${this.getAccessibilityContent(element, true)}。`;
                });
            } else {
                const content = this.getAccessibilityContent(selectedModel as IElementBaseModel, true);
                selectedContent = i18n.t('已选中，{0}。', { 0: content });
            }
            Accessibility.readStr(selectedContent);
        }
    }

    // 监听播放时幻灯片索引的变化
    private handlePlayIndexChange(index: number) {
        if (this.isJustStartPlay) {
            this.timer && clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.isJustStartPlay = false;
                this.timer = null;
            }, 50);
            this.readSlide(this.services.selectionService.slides[index], index);
        } else {
            this.readSlide(this.services.selectionService.slides[index], index, true);
        }
    }

    // 确定modal文案
    private getModalContent(isShortCut?: boolean) {
        let contentLabel: string;
        const closeStr = i18n.t('确定关闭屏幕阅读器支持能力吗？');
        const openStr = i18n.t('开启屏幕阅读器支持，读屏软件可读出文档内容和编辑操作，便于障碍人士使用。');
        const isEnableState = Accessibility.isEnable();
        const contentStr = isEnableState ? closeStr : openStr;
        if (isShortCut) {
            const closeLabel = i18n.t('确定关闭屏幕阅读器支持能力吗？确定请按Enter，取消请按Esc。');
            const openLabel = i18n.t('开启屏幕阅读器支持，读屏软件可读出文档内容和编辑操作，便于障碍人士使用。确定请按Enter，取消请按Esc。');
            contentLabel = isEnableState ? closeLabel : openLabel;
        } else {
            contentLabel = contentStr;
        }
        const content = <div aria-label={`。${contentLabel}`}>
            <div aria-hidden={true}>
                {contentStr}
            </div>
        </div>;
        return content;
    }

    // 读出粘贴操作
    private handlePaste() {
        Accessibility.readStr(i18n.t('粘贴'));
    }

    /**
     * 读出箭头移动选中元素的操作
     * @param left 按下左←或右→箭头
     * @param top 按下上↑或者下↓箭头
     */
    private handleSelectedEleWithArrow(left: number, top: number): void {
        if (this.arrowMoveTimer) {
            clearTimeout(this.arrowMoveTimer);
        }
        this.arrowMoveTimer = setTimeout(() => {
            this.readMoveEleWithArrow(left, top);
        }, 500);
    }

    private readMoveEleWithArrow = (left: number, top: number): void => {
        let distance;
        let direction;
        if (top !== 0) {
            distance = top;
            direction = top > 0 ? i18n.t('下') : i18n.t('上');
        }
        if (left !== 0) {
            distance = left;
            direction = left > 0 ? i18n.t('右') : i18n.t('左');
        }
        if (direction) {
            distance = convertPxToCm(Math.abs(distance));
            Accessibility.readStr(i18n.t('已向{0}微移{1}厘米。', { 0: direction, 1: distance }));
        }
    };

    /**
     * 朗读编辑态下，光标左移的内容
     */
    private readLeftKey = (endOffset: number, text: string): string => {
        let content = i18n.t('换行');
        if (!this.lastText || this.lastText === text) {
            content = text.slice(endOffset, endOffset + 1);
        }
        return content;
    };

    /**
     * 朗读编辑态下，光标右移的内容
     */
    private readRightKey = (endOffset: number, text: string): string => {
        let content = text.slice(endOffset - 1, endOffset);
        if (endOffset === 0) {
            content = i18n.t('换行');
        }
        return content;
    };

    private getAccessibilityContent(model: IElementBaseModel, isSelected = false, content = ''): string {
        // 不能成组的元素
        const ContentMap = {
            [UIModelType.TABLE]: TableAccessibilityContent,
        };
        if (ContentMap[model.type]) {
            const ContentModel = ContentMap[model.type];
            return new ContentModel(model, this.services.addonManager).getAccessibilityContent(isSelected, content);
        }
        // 能够成组的元素
        const canBeGroupChildContent = GroupAccessibilityContent
            .getCanBeGroupChildAccessibilityContent(model, isSelected, content, this);
        // 其他情况使用base兜底
        return canBeGroupChildContent ?? new ElementBaseAccessibilityContent(model, this)
            .getAccessibilityContent(isSelected, content);
    }
}
